import { useEffect, useRef, useState } from 'react';
import type { FormEvent } from 'react';

import type { TFunction } from 'i18next';
import { computed } from 'mobx';
import { observer } from 'mobx-react';
import { Trans, useTranslation } from 'react-i18next';

import Button from '../../../components/buttons/Button';
import { DialogContent } from '../../../components/Dialog/DialogContent';
import { DialogHeader } from '../../../components/Dialog/DialogHeader';
import { DialogLoader } from '../../../components/Dialog/DialogLoader';
import { ErrorMessage } from '../../../components/ErrorMessage/ErrorMessage';
import { FormInput } from '../../../components/FormInput/FormInput';
import { PasswordInput } from '../../../components/FormInput/PasswordInput';
import type { ErrorMessageMap } from '../../../components/FormInput/types';
import type { WizardChildProps } from '../../../components/Wizard/types';
import { analytics } from '../../../services/analytics';
import { prewarmRecaptcha } from '../../../services/firebase';
import { useStores } from '../../../stores/useStores';
import { ForceResetError } from '../forceReset/ForceResetError';
import { useForceReset } from '../forceReset/useForceReset';
import { RecaptchaNotice } from '../RecaptchaNotice/RecaptchaNotice';
import { ThirdPartyAuth } from '../ThirdPartyAuth/ThirdPartyAuth';
import { useHandleAuth } from '../useHandleAuth';
import { useOpenAuthWizard } from '../useOpenAuthWizard';

import styles from './LogIn.module.scss';

interface LoginForm extends HTMLFormElement {
  elements: HTMLFormControlsCollection & {
    email: HTMLInputElement;
    password: HTMLInputElement;
  };
}
const emailErrorMessageMap = (t: TFunction): ErrorMessageMap => ({
  typeMismatch: t('validation.emailInvalid'),
  valueMissing: t('validation.emailRequired'),
});

const passwordErrorMessageMap = (t: TFunction): ErrorMessageMap => ({
  valueMissing: t('validation.passwordRequired'),
});

export const LogIn = observer((props: WizardChildProps) => {
  const { t } = useTranslation();
  const { authStore, portalStore } = useStores();

  const { openAuthWizard } = useOpenAuthWizard();

  const landingKey = authStore.getLandingKey();
  const isDirectLanding = authStore.isDirectLanding();

  const [error, setError] = useState<string>();

  useEffect(() => {
    void prewarmRecaptcha();
  }, []);

  const loginImpressionFiredRef = useRef(false);

  useEffect(() => {
    if (props.isCurrent && !loginImpressionFiredRef.current) {
      loginImpressionFiredRef.current = true;
      void analytics.sendEvent('login_page', {
        event_category: 'sign_up',
        event_action: 'impression',
      });
    } else if (!props.isCurrent) {
      loginImpressionFiredRef.current = false;
    }
  }, [props.isCurrent]);

  const { handleAuth: runAuth } = useHandleAuth(setError, props.nextStep);
  const { initialEmail, handleAuth, isForceReset, clearForceReset, startResetPassword } = useForceReset(runAuth);

  const clearError = () => {
    setError(undefined);
    clearForceReset();
  };

  const isProcessing = computed(() => authStore.pending('signIn')).get();

  const openResetPassword = () =>
    startResetPassword(() =>
      openAuthWizard('resetPasswordWizard', {
        force: true,
        persistent: isDirectLanding,
        landingKey: landingKey ?? undefined,
      }),
    );

  const handleSubmit = (event: FormEvent<LoginForm>) => {
    event.preventDefault();
    const { email, password } = event.currentTarget.elements;
    void analytics.sendEvent('login_account', {
      event_category: 'sign_up',
      event_action: 'click',
      event_type: 'login_platform',
      event_value: 'email',
      button_text: 'log in',
    });

    void handleAuth({
      provider: 'email',
      action: 'log_in',
      credentials: {
        email: email.value,
        password: password.value,
      },
    });
  };

  const onGoBack = () => {
    void analytics.sendEvent('signup_account_button', {
      event_category: 'sign_up',
      event_action: 'click',
      button_text: 'sign up',
    });

    openAuthWizard('signUpWizard', {
      triggeredBy: 'log_in',
      force: true,
      persistent: isDirectLanding,
      landingKey: landingKey ?? undefined,
    });
  };

  return (
    <>
      <DialogHeader
        closeBtnDataTestId="close-login"
        title={t('auth.logIn')}
        onClose={portalStore.closePortal}
        onGoBack={isDirectLanding ? onGoBack : undefined}
      />

      <DialogLoader isProcessing={isProcessing} />

      <DialogContent className={styles.loginDialogContent}>
        <ThirdPartyAuth action="Log in" />

        <div className={styles.delimiter}>
          <hr />
          <span>{t('common.orLowerCase')}</span>
          <hr />
        </div>

        <form onChange={clearError} onSubmit={handleSubmit}>
          <div className={styles.formRow}>
            <label className="form-field__label" htmlFor="email">
              {t('common.email')}
            </label>
            <FormInput
              required
              autoComplete="email"
              data-testid="email-input"
              defaultValue={initialEmail}
              disabled={isProcessing}
              errorMessageMap={emailErrorMessageMap(t)}
              id="email"
              name="email"
              placeholder={t('forms.emailEmail')}
              type="email"
            />
          </div>

          <div className={styles.formRow}>
            <label className="form-field__label" htmlFor="password">
              {t('forms.password')}
            </label>
            <PasswordInput
              required
              disabled={isProcessing}
              errorMessageMap={passwordErrorMessageMap(t)}
              id="password"
              name="password"
              placeholder={t('auth.enterPassword')}
            />
          </div>

          <Button
            btnType="main"
            className={styles.logInButton}
            dataTestId="login-submit-btn"
            disabled={isProcessing || isForceReset}
            type="submit"
          >
            {t('auth.logIn')}
          </Button>

          {isForceReset ? (
            <ForceResetError message={error} onResetPassword={openResetPassword} />
          ) : (
            <>
              <ErrorMessage>{error}</ErrorMessage>

              <div className="animation-slide-down form-links" data-testid="login-forgot-password-line">
                <Trans
                  components={{
                    question: <span />,
                    reset: (
                      <span
                        className="text-link"
                        data-testid="login-forgot-password-link"
                        onClick={openResetPassword}
                      />
                    ),
                  }}
                  i18nKey="auth.forgotPasswordClickHere"
                />
              </div>
            </>
          )}
          <RecaptchaNotice />
        </form>
      </DialogContent>
    </>
  );
});
